import { useState, type ReactNode } from 'react';
import { Building2, User, X } from 'lucide-react';

export interface ScopeCompany {
  id: string;
  slug: string;
  name: string;
}

/** Fila seleccionable tipo radio (una de las dos opciones de alcance). */
function RadioRow({
  selected,
  onSelect,
  icon,
  title,
  subtitle,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`rounded-xl border-2 p-3 transition ${selected ? 'border-red-500 bg-red-50/50' : 'border-slate-200'}`}>
      <button type="button" onClick={onSelect} className="flex w-full items-center gap-3 text-left">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
            selected ? 'border-red-600' : 'border-slate-300'
          }`}
        >
          {selected && <span className="h-2.5 w-2.5 rounded-full bg-red-600" />}
        </span>
        {icon}
        <span>
          <span className="block text-sm font-semibold text-slate-700">{title}</span>
          {subtitle && <span className="block text-xs text-slate-500">{subtitle}</span>}
        </span>
      </button>
      {children}
    </div>
  );
}

/** Copy según el uso: guardar un cambio vs restaurar al default. */
const COPY = {
  save: {
    title: '¿Dónde guardar los cambios?',
    desc: 'Elige el alcance de este cambio y confirma.',
    userTitle: 'Solo para mí',
    userSub: 'Mi vista personal (no afecta a nadie más)',
    companyTitle: 'Para toda la compañía',
    companySub: 'El default de quienes no tengan tablero propio',
    confirm: 'Guardar',
  },
  restore: {
    title: '¿Qué tablero restaurar?',
    desc: 'Quita las personalizaciones y vuelve al default.',
    userTitle: 'Solo el mío',
    userSub: 'Vuelvo al default de mi compañía o del sistema',
    companyTitle: 'El de la(s) compañía(s)',
    companySub: 'Borra el default publicado — todos vuelven al del sistema',
    confirm: 'Restaurar',
  },
} as const;

/**
 * Modal admin: elige el ALCANCE (radio) — solo para mí, o 1+ compañías (checkboxes) — y
 * confirma. `variant` cambia el copy (guardar vs restaurar). Las empresas llegan ya filtradas
 * por el caller. El BFF re-valida el permiso.
 */
export function ScopeSaveModal({
  companies,
  onConfirm,
  onClose,
  variant = 'save',
}: {
  companies: ScopeCompany[];
  onConfirm: (scope: 'user' | 'company', companyIds?: number[]) => void;
  onClose: () => void;
  variant?: 'save' | 'restore';
}) {
  const t = COPY[variant];
  const [mode, setMode] = useState<'user' | 'company'>('user');
  const [checked, setChecked] = useState<Set<string>>(() => new Set(companies.map((c) => c.id)));
  const toggle = (id: string) =>
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const ids = companies.filter((c) => checked.has(c.id)).map((c) => Number(c.id));
  const canSave = mode === 'user' || ids.length > 0;
  const save = () => (mode === 'user' ? onConfirm('user') : onConfirm('company', ids));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between p-5 pb-1">
          <h3 className="text-lg font-bold text-slate-800">{t.title}</h3>
          <button onClick={onClose} aria-label="Cerrar">
            <X className="h-5 w-5 text-slate-400 hover:text-slate-700" />
          </button>
        </div>
        <p className="px-5 text-sm text-slate-500">{t.desc}</p>

        <div className="space-y-2 p-5 pt-3">
          <RadioRow
            selected={mode === 'user'}
            onSelect={() => setMode('user')}
            icon={<User className="h-5 w-5 shrink-0 text-slate-500" />}
            title={t.userTitle}
            subtitle={t.userSub}
          />

          {companies.length > 0 && (
            <RadioRow
              selected={mode === 'company'}
              onSelect={() => setMode('company')}
              icon={<Building2 className="h-5 w-5 shrink-0 text-slate-500" />}
              title={t.companyTitle}
              subtitle={t.companySub}
            >
              {mode === 'company' && (
                <div className="mt-2 space-y-0.5 pl-8">
                  {companies.map((c) => (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-white"
                    >
                      <input
                        type="checkbox"
                        checked={checked.has(c.id)}
                        onChange={() => toggle(c.id)}
                        className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-600"
                      />
                      <span className="text-sm text-slate-700">{c.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </RadioRow>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 p-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {t.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
